import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Formik, type FormikTouched, setNestedObjectValues } from "formik";

import { useUserProfile } from "@vega-api/extended/auth/hooks";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import {
  getGetAllDealsV2QueryKey,
  useCreateDealV2,
} from "@vega-api/generated/deal/deal";
import { Currency } from "@vega-api/generated/types/currency";
import { UserPartnershipPartnerType } from "@vega-api/generated/types/userPartnershipPartnerType";
import { DropdownField } from "@vega-core/components/Form/Controls/Dropdown/Dropdown/DropdownField";
import { InputField } from "@vega-core/components/Form/Fields/InputField";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { useToast } from "@vega-core/components/Toast/useToast";
import { coreCurrencies } from "@vega-core/models/money";
import * as m from "@vega-core/paraglide/messages.js";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";
import { CoInvestmentFullPaths } from "@vega-modules/paths";

import { getCoInvestmentValidationSchema } from "../../validations/coInvestmentValidation";

export type CoInvestmentFormData = {
  name: string;
  assetClass?: string;
  currency?: Currency;
  fundName?: string;
  region?: string;
  sector?: string;
};

export type CreateCoInvestmentModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

export const CreateCoInvestmentModal = ({
  open,
  setOpen,
}: CreateCoInvestmentModalProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const { featureFlag } = useFeatureFlag();
  const isNewNavEnabled =
    featureFlag[FeatureFlag.GP_COINVEST_NEW_UI_NAV_VD_25769];

  const { data: userProfile } = useUserProfile();

  const createDealMutation = useCreateDealV2({
    mutation: {
      onSuccess: (response) => {
        const dealId = response.data?.id;

        showToast({
          variant: "success",
          title: m.common_saveChangesSuccess(),
        });

        queryClient.invalidateQueries({
          queryKey: getGetAllDealsV2QueryKey(),
        });

        setOpen(false);

        if (dealId) {
          const destination = isNewNavEnabled
            ? CoInvestmentFullPaths.CoInvestmentConfigure.replace(":id", dealId)
            : CoInvestmentFullPaths.CoInvestmentDetails.replace(":id", dealId);

          navigate(destination);
        }
      },
      onError: (error) => {
        showErrorToast(error, {
          defaultMessage: m.coInvestments_error_createDeal(),
        });
      },
    },
  });

  const initialValues: CoInvestmentFormData = {
    name: "",
    currency: Currency.USD,
    fundName: "",
  };

  const handleSubmit = (values: CoInvestmentFormData) => {
    const generalPartnerId = userProfile?.partnerships.find(
      (partnership) =>
        partnership.partnerType === UserPartnershipPartnerType.GENERAL,
    )?.partnerId;

    if (!generalPartnerId) {
      showToast({
        variant: "destructive",
        title: m.errors_noGeneralPartner(),
      });

      return;
    }

    const dealRequest = {
      name: values.name,
      generalPartnerId,
      ...(values.fundName && { fundName: values.fundName }),
      assetClass: values.assetClass as string,
      region: values.region,
      sector: values.sector,
      currency: values.currency as Currency,
    };

    createDealMutation.mutate({ data: dealRequest });
  };

  return (
    <Formik
      initialValues={initialValues}
      key={open ? "open" : "closed"}
      onSubmit={handleSubmit}
      validationSchema={getCoInvestmentValidationSchema()}
    >
      {({ handleSubmit: formikHandleSubmit, validateForm, setTouched }) => (
        <ActionaryModal
          ModalButtonsGroupProps={{
            mainActionText: m.btn_create(),
            secondaryActionText: m.btn_cancel(),
            disabled: createDealMutation.isPending,
            mainAction: async () => {
              const errors = await validateForm();

              if (Object.keys(errors).length === 0) {
                formikHandleSubmit();
              } else {
                setTouched(
                  setNestedObjectValues<FormikTouched<CoInvestmentFormData>>(
                    errors,
                    true,
                  ),
                );
              }
            },
            secondaryAction: () => setOpen(false),
          }}
          dataTestId="create-co-investment-modal"
          open={open}
          setOpen={setOpen}
          title={m.common_basicInformation()}
        >
          <form
            className="space-y-6 mt-6 text-left max-w-md"
            onSubmit={formikHandleSubmit}
          >
            <InputField
              label={m.common_name()}
              name="name"
              placeholder={m.placeholders_enter({
                name: m.common_name().toLowerCase(),
              })}
            />
            <DropdownField
              label={m.common_currency()}
              name="currency"
              options={coreCurrencies.map((c) => ({
                value: c,
                label: c,
              }))}
              placeholder={m.placeholders_select({
                name: m.common_currency().toLowerCase(),
              })}
            />

            <InputField
              label={m.common_fund()}
              name="fundName"
              placeholder={m.placeholders_enter({
                name: m.common_fund().toLowerCase(),
              })}
            />
          </form>
        </ActionaryModal>
      )}
    </Formik>
  );
};
